/**
 * How a service's recipe turns a measure into a quantity.
 *
 * One formula, everywhere:
 *
 *     qty = base_qty + qty_per_unit × measure
 *
 * and a band may replace the two numbers for a given range of the measure.
 * That is the whole of it, and it is deliberately small: the same arithmetic
 * runs when a service is added to a quote, when the measure is changed, and
 * when the scaling card previews what a change would do — three places that
 * must agree exactly or the card is a guess about what the button will do.
 *
 * **Why a base, and not just a rate.** A flat conversion prices a small job as
 * a fraction of a big one, which is not how work behaves: a crew mobilises,
 * unloads and sets up whether the patio is 200 square feet or 2,000. The base
 * is that fixed part. It also subsumes two things that used to be special
 * cases — a flat conversion is a base of zero, and a "once per job" line
 * (`deployment_costs.once_per_job`) is a conversion of zero.
 *
 * **Why a band stores a floor and no ceiling.** Two numbers per band let
 * somebody write 0–3,000 and 3,001–5,000 and leave 3,000.5 in a gap that
 * resolves to nothing. A band here runs to the next band's floor, so gaps and
 * overlaps cannot be expressed rather than being validated after the fact.
 *
 * **The base is the mechanism; it is not automatically the cure.** Banding a
 * flat conversion creates a cliff: at 0.0040 h/sq ft up to 3,000 and 0.0030
 * above it, a 3,000 sq ft job budgets 12.00 h and a 3,001 sq ft job budgets
 * 9.00 h, and the bigger job is three hours cheaper. A base lets the total
 * carry across the edge instead — but only if it is big enough. For the total
 * not to drop at a floor *f*, the next band's base has to make up what the
 * lower rate gives away:
 *
 *     base₂ ≥ base₁ + (rate₁ − rate₂) × f
 *
 * which is not obvious while typing a table, and is exactly the mistake the
 * first draft of this file's own worked example made. `bandsAreMonotonic`
 * checks every edge from both sides so the catalog can say which one goes
 * backwards and by how much, rather than leaving it to be discovered on a
 * quote.
 */
/**
 * The band a measure falls in, or the line's own numbers below the lowest one.
 *
 * Below the first band the line itself applies, so a line is always priceable
 * and a tier set is an addition rather than a prerequisite. The catalog editor
 * starts the first band at 0, so in practice that fallback is only reached by
 * a line nobody has banded at all.
 */
export function resolveBand(rule, measure) {
    let best = null;
    for (const band of rule.bands) {
        if (band.minMeasure > measure)
            continue;
        if (!best || band.minMeasure > best.minMeasure)
            best = band;
    }
    // `from` is the floor that applied, so the scaling card can say "3,000+"
    // without working it out a second way and risking a different answer.
    return best
        ? { baseQty: best.baseQty, qtyPerUnit: best.qtyPerUnit, from: best.minMeasure }
        : { baseQty: rule.baseQty, qtyPerUnit: rule.qtyPerUnit, from: null };
}
/**
 * What a measure produces, rounded the way the column holds it.
 *
 * Three places, because `numeric(14,3)` is three places — and never silently
 * to zero, because 0.0222 ton per sq ft over 4 sq ft is 0.089 ton and a line
 * that rounded to nothing would price at nothing and buy nothing. A measure of
 * zero is the exception: a service added before anybody has walked the site
 * expands at its bases, and a column of 0.001s priced at four cents each reads
 * like a bug rather than like "not measured yet".
 */
export function quantityFor(rule, measure) {
    const band = resolveBand(rule, measure);
    const raw = band.baseQty + band.qtyPerUnit * measure;
    if (raw <= 0)
        return 0;
    return Math.max(0.001, Math.round(raw * 1000) / 1000);
}
/**
 * Whether a bigger measure always costs at least as much as a smaller one.
 *
 * Checked at every band edge, on both sides: a band boundary is the only place
 * the function can step down, because inside a band it is a line with a
 * non-negative slope. Returns the first edge that goes backwards so the editor
 * can say which one and by how much.
 */
export function bandsAreMonotonic(rule) {
    const floors = [...new Set(rule.bands.map((band) => band.minMeasure))].sort((a, b) => a - b);
    for (const floor of floors) {
        if (floor <= 0)
            continue;
        /*
         * Just under the edge against exactly on it. A thousandth is the smallest
         * step a `numeric(14,3)` measure can take, so this is the real pair of
         * adjacent measures rather than a notional limit.
         */
        const before = quantityFor(rule, Math.round((floor - 0.001) * 1000) / 1000);
        const after = quantityFor(rule, floor);
        if (after < before)
            return { ok: false, at: floor, before, after };
    }
    return { ok: true };
}
/** The scale `service_line_tiers.base_qty` holds (`numeric(14,6)`). */
const BASE_SCALE = 1_000_000;
/** The smallest step a `numeric(14,3)` measure takes — a band starts one past its ceiling. */
const MEASURE_STEP = 0.001;
/** Up to the column's scale, never down: a derived base is a floor, not an estimate. */
function baseUp(value) {
    return Math.ceil(Math.round(value * BASE_SCALE * 1000) / 1000) / BASE_SCALE;
}
/**
 * Stacked tiers → the bands the tables store (§6.8 item 4). The one
 * derivation; nothing else turns tiers into bands.
 *
 *     tier 1        → the line's own base and rate (below the first band the line applies)
 *     tier k ≥ 2    → { minMeasure: ceiling(k−1) + 0.001, qtyPerUnit: rate(k),
 *                       baseQty: base(k−1) + (rate(k−1) − rate(k)) × ceiling(k−1) }
 *
 * The base is the one that makes the quantity meet itself at each ceiling —
 * `base₂ = base₁ + (rate₁ − rate₂) × f`, the equality case of the rule
 * `bandsAreMonotonic` checks — so a tier set made here always passes it.
 */
export function tiersToBands(line, tiers) {
    const bands = [];
    let base = line.baseQty;
    let rate = line.qtyPerUnit;
    for (let at = 1; at < tiers.length; at += 1) {
        const ceiling = tiers[at - 1].upTo;
        if (ceiling === null)
            break;
        const next = tiers[at].qtyPerUnit;
        base = baseUp(base + (rate - next) * ceiling);
        rate = next;
        bands.push({
            minMeasure: Math.round((ceiling + MEASURE_STEP) * 1000) / 1000,
            baseQty: base,
            qtyPerUnit: rate,
        });
    }
    return bands;
}
/**
 * Stored bands → the tiers they were made from, or `null` when they were not
 * made that way (§6.8 item 5): bases somebody typed by hand in the old table,
 * a first band at 0, a band whose base is not the continuous one. A `null`
 * keeps the old table open for that line rather than converting anything
 * behind anybody's back.
 */
export function bandsToTiers(rule) {
    const bands = [...rule.bands].sort((a, b) => a.minMeasure - b.minMeasure);
    const tiers = [];
    let base = rule.baseQty;
    let rate = rule.qtyPerUnit;
    let floor = 0;
    for (const band of bands) {
        const ceiling = Math.round((band.minMeasure - MEASURE_STEP) * 1000) / 1000;
        if (ceiling <= floor)
            return null;
        const expected = baseUp(base + (rate - band.qtyPerUnit) * ceiling);
        if (Math.abs(expected - band.baseQty) > 1 / BASE_SCALE)
            return null;
        tiers.push({ upTo: ceiling, qtyPerUnit: rate });
        base = band.baseQty;
        rate = band.qtyPerUnit;
        floor = ceiling;
    }
    tiers.push({ upTo: null, qtyPerUnit: rate });
    return tiers;
}
/**
 * The four sizes the tiers editor's check strip shows (§6.8 item 3) — chosen,
 * never typed: the sample, the first ceiling, halfway between the first two
 * ceilings and 1.2 × the last; with one ceiling, the sample, it, 1.5 × and 2 ×.
 */
export function tierCheckSizes(sample, tiers) {
    const ceilings = tiers.map((tier) => tier.upTo).filter((upTo) => upTo !== null);
    if (ceilings.length === 0)
        return [sample];
    const first = ceilings[0];
    if (ceilings.length === 1)
        return [sample, first, first * 1.5, first * 2];
    const last = ceilings[ceilings.length - 1];
    return [sample, first, (first + ceilings[1]) / 2, Math.round(last * 1.2 * 1000) / 1000];
}
/**
 * `scales_by`, derived on save rather than chosen (§6.8 item 2): a rate of 0
 * is *once*, anything else grows with the measure — or with a quantity, where
 * a package is asked for one rather than a measurement.
 */
export function scalesByOf(qtyPerUnit, asked = 'measurement') {
    return qtyPerUnit === 0 ? 'once' : asked;
}
//# sourceMappingURL=service-quantity.js.map